import BaseChatbotService from './base-chatbot';
import loadScript from 'later/utils/load-script';

import type { UserAndAccountTraits } from './chatbot-wrapper';

type ConversationFields = Record<string, any> | undefined;

const FIELD_ID_MAP: Record<string, string> = {
  account_owner: '30740753365783',
  activated_trial: '30740798618391',
  active_trial: '30740821694487',
  ai_credits_remaining: '30967237708055',
  ai_credits_total: '30967255557015',
  ai_credits_used: '30967257297175',
  annual_user_initial_order_within_thirty_days: '30740807988119',
  annual_user_renewed_over_thirty_days: '30742428583959',
  billing_email: '30742399022359',
  browser: '30967285285655',
  browser_version: '30967286572695',
  bttp_enabled: '30967274073367',
  current_free_rollout: '30967288951831',
  current_plan_rollout: '30742406618647',
  device: '30742439844375',
  email: '30742454936727',
  follower_count: '30742456654231',
  facebook: '30967331166103',
  facebook_posts_remaining: '30967345958935',
  facebook_posts_total: '30967316827927',
  facebook_posts_used: '30967320779031',
  first_thirty_days: '30967349394839',
  has_business_profile: '30742464448791',
  has_creator_profile: '30742480974359',
  has_opened_chatbot_previously: '30742482354711',
  has_active_cancelled_subscription: '30742484042391',
  has_active_uncancelled_subscription: '30742529483799',
  initial_free_rollout: '30742532129559',
  initialurl: '30742517925015',
  instagram_follow_count: '30742518849687',
  instagram_posts_remaining: '30967309018647',
  instagram_posts_total: '30967325729175',
  instagram_posts_used: '30967356710551',
  instagram_profile_name: '30967395212823',
  instagram_profile_status: '30967342761879',
  instagram_profile_type: '30967374558615',
  language: '30967389359383',
  linkedin: '30967376561047',
  linkedin_posts_remaining: '30967445244183',
  linkedin_posts_total: '30967393504791',
  linkedin_posts_used: '30967433316631',
  linkinbio_set_up: '30967421868823',
  monthly_user_completed_order_over_seven_days: '30742535346455',
  more_than_5_social_sets: '30967423245207',
  name: '30742527829271',
  number_of_social_sets: '30967437531671',
  number_of_users: '30967468495767',
  opted_in_influencers: '30742563584279',
  pinterest: '30967440485783',
  pinterest_posts_remaining: '30967473398935',
  pinterest_posts_total: '30967474872471',
  pinterest_posts_used: '30967498423191',
  plan_interval: '30742549631639',
  plan_name: '30742581731351',
  'joined-creator-database': '30742568273687',
  plan_type: '30742585116055',
  subscription_provider: '30742614935575',
  support_segment: '30742638805783',
  threads: '30967484474775',
  threads_posts_remaining: '30967529208855',
  threads_posts_total: '30967514014103',
  threads_posts_used: '30967547955863',
  tiktok: '30967505989527',
  tiktok_posts_remaining: '30967517466775',
  tiktok_posts_total: '30967534560407',
  tiktok_posts_used: '30967536588823',
  trial_count: '30742633102103',
  twitter: '30967554240407',
  twitter_posts_remaining: '30967539304855',
  twitter_posts_total: '30967581842327',
  twitter_posts_used: '30967589242775',
  user_agent: '30967584296087',
  user_id: '30742620451735',
  youtube: '30967645475607',
  youtube_posts_remaining: '30967602645527',
  youtube_posts_total: '30967648050583',
  youtube_posts_used: '30967663160727',
  stripe_customer_id: '30967616065559',
  mavely_account: '30742628725911',
  creator_profile_id: '30967651925399'
};

export default class ZendeskService extends BaseChatbotService {
  readonly name = 'zendesk';

  async setup(userAndAccountTraits: UserAndAccountTraits): Promise<void> {
    // Note: canChatbot is false for certain staging QA users to prevent
    // widget from interfering with locators on auto tests
    if (!this.auth.currentAccount?.canChatbot || this.auth.currentAccount?.rolloutMavenChatbot) {
      return;
    }

    try {
      await loadScript('https://static.zdassets.com/ekr/snippet.js?key=d2549f48-39e9-4bce-b364-3743deab0de8', {
        id: 'ze-snippet'
      });
      this.setConversationFields(userAndAccountTraits);

      if (window.zE) {
        window.zE('messenger:on', 'open', async () => {
          this.setConversationFields(userAndAccountTraits);
          this.onOpen();
        });

        window.zE('messenger:on', 'close', () => {
          this.onClose();
        });
      }
    } catch (error) {
      this.errors.log('Zendesk failed to load', error);
      this.displayError();
    }
  }

  show(): void {
    window.zE?.('messenger', 'show');
  }

  hide(): void {
    window.zE?.('messenger', 'hide');
  }

  open(): void {
    if (window.zE) {
      window.zE?.('messenger', 'open');
    }
  }

  close(): void {
    window.zE?.('messenger', 'close');
  }

  reset(): void {
    window.zE?.('messenger', 'logoutUser');
  }

  trackEvent(name: string): void {
    window.zE('messenger:track', name);
  }

  setConversationFields(conversationFields: ConversationFields): void {
    if (conversationFields) {
      const conversationFieldsArray = Object.keys(conversationFields)
        .filter((key) => !!FIELD_ID_MAP[key])
        .map((key) => ({
          id: FIELD_ID_MAP[key],
          value: String(conversationFields[key])
        }));

      window.zE('messenger:set', 'conversationFields', conversationFieldsArray);
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'chatbot/zendesk': ZendeskService;
  }
}
